import { useState, useEffect, useMemo, useCallback } from 'react';
import { notify } from '../../utils/toast';
import type {
    ClientProfile,
    WhatsappConnection,
    MetaAccount,
    Subscription,
    SubscriptionPlan,
} from '../../types';
import type { Organization } from '../OrganizationContext';
import {
    getSubscriptionPlans,
    getClientProfile,
    getWhatsappConnections,
    getMetaAccounts,
    getCurrentSubscription,
    saveClientProfile as persistClientProfile,
} from '../../services/crmService';

/**
 * Carrega e mantém dados de CRM escopados à organização ativa
 * (perfil, conexões, Meta, assinatura + planos).
 */
export function useCrmOrgData(
    currentOrganization: Organization | null,
    usedSeats: number
) {
    const [subscriptionPlans, setSubscriptionPlans] = useState<SubscriptionPlan[]>([]);
    const [clientProfile, setClientProfile] = useState<ClientProfile | null>(null);
    const [whatsappConnections, setWhatsappConnections] = useState<WhatsappConnection[]>([]);
    const [metaAccounts, setMetaAccounts] = useState<MetaAccount[]>([]);
    const [subscription, setSubscription] = useState<Subscription | null>(null);
    const [isLoading, setIsLoading] = useState(true);

    const refreshWhatsappConnections = useCallback(async () => {
        if (!currentOrganization?.id) return;
        try {
            const whatsappData = await getWhatsappConnections(currentOrganization.id);
            setWhatsappConnections(whatsappData);
        } catch (error) {
            console.error('[useCrmOrgData] Failed to refresh WhatsApp connections:', error);
        }
    }, [currentOrganization?.id]);

    useEffect(() => {
        getSubscriptionPlans()
            .then(setSubscriptionPlans)
            .catch((err) => console.error('[useCrmOrgData] Failed to load subscription plans:', err));
    }, []);

    useEffect(() => {
        const onWhatsappUpdated = () => {
            void refreshWhatsappConnections();
        };
        window.addEventListener('crm:whatsapp-connections-updated', onWhatsappUpdated);
        return () => window.removeEventListener('crm:whatsapp-connections-updated', onWhatsappUpdated);
    }, [refreshWhatsappConnections]);

    useEffect(() => {
        const onBillingUpdated = async () => {
            if (!currentOrganization?.id) return;
            try {
                const subscriptionData = await getCurrentSubscription(currentOrganization.id);
                setSubscription(subscriptionData);
            } catch (error) {
                console.error('[useCrmOrgData] Failed to refresh subscription:', error);
            }
        };
        window.addEventListener('crm:billing-updated', onBillingUpdated);
        return () => window.removeEventListener('crm:billing-updated', onBillingUpdated);
    }, [currentOrganization?.id]);

    useEffect(() => {
        if (!currentOrganization) {
            setIsLoading(false);
            return;
        }

        let cancelled = false;

        const loadData = async () => {
            setIsLoading(true);
            try {
                const [profileData, whatsappData, metaData, subscriptionData] = await Promise.all([
                    getClientProfile(currentOrganization.id),
                    getWhatsappConnections(currentOrganization.id),
                    getMetaAccounts(),
                    getCurrentSubscription(currentOrganization.id),
                ]);
                if (cancelled) return;
                setClientProfile(profileData);
                setWhatsappConnections(whatsappData);
                setMetaAccounts(metaData);
                setSubscription(subscriptionData);
            } catch (error) {
                if (!cancelled) {
                    console.error('[useCrmOrgData] Failed to load org data:', error);
                }
            } finally {
                if (!cancelled) setIsLoading(false);
            }
        };

        void loadData();
        return () => {
            cancelled = true;
        };
    }, [
        currentOrganization?.id,
        currentOrganization?.plan,
        currentOrganization?.subscriptionStatus,
        currentOrganization?.status,
    ]);

    const syncedSubscription = useMemo(() => {
        if (!subscription) return null;
        return {
            ...subscription,
            usedSeats,
            totalSeats: Math.max(subscription.totalSeats, usedSeats),
        };
    }, [subscription, usedSeats]);

    const saveClientProfile = useCallback(async (profile: ClientProfile) => {
        if (!currentOrganization) {
            notify.error('Nenhuma organização ativa. Recarregue a página.');
            return;
        }
        try {
            await persistClientProfile(profile, currentOrganization.id);
            setClientProfile(profile);
            notify.success('Perfil salvo com sucesso!');
        } catch (error: unknown) {
            const message = error instanceof Error ? error.message : 'Falha ao salvar perfil.';
            notify.error(message);
        }
    }, [currentOrganization]);

    const updateSubscription = useCallback((
        planId: string,
        userCount: number,
        billingCycle: 'monthly' | 'annually'
    ) => {
        const selectedPlan = subscriptionPlans.find((p) => p.id === planId);
        if (!selectedPlan || !subscription) return;

        setSubscription({
            ...subscription,
            planName: selectedPlan.name,
            pricePerSeat: parseFloat(selectedPlan.price.replace(',', '.')),
            totalSeats: userCount,
            billingType: billingCycle === 'monthly' ? 'MENSALMENTE' : 'ANUALMENTE',
            isLegacy: false,
        });
    }, [subscriptionPlans, subscription]);

    return {
        subscriptionPlans,
        clientProfile,
        whatsappConnections,
        metaAccounts,
        subscription: syncedSubscription,
        isLoading,
        saveClientProfile,
        updateSubscription,
    };
}
